import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Field, useFormState } from "react-final-form";
import {
  Autocomplete,
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Tooltip,
} from "@mui/material";
import DeleteOutlined from "@mui/icons-material/DeleteOutlined";
import EditOutlined from "@mui/icons-material/EditOutlined";
import KeyboardArrowUpOutlined from "@mui/icons-material/KeyboardArrowUpOutlined";
import LocationOffOutlined from "@mui/icons-material/LocationOffOutlined";
import SearchOutlined from "@mui/icons-material/SearchOutlined";
import debounce from "lodash/debounce";
import get from "lodash/get";
import Flx from "../../layout/Flx";
import FlxGrid from "../../layout/FlxGrid";
import Txt from "../../typography/Txt";
import colors, { INPUT_BORDER_RADIUS } from "../../../theme/colors";
import useEnv from "../../../hooks/useEnv";
import { selectOptionsUsaStates } from "../../../constants/selectOptions/selectOptionsUsaStates";
import formatAddressObjectFullAddress from "../../../_src_shared/utils/functions/formatAddressObjectFullAddress";
import RffInputWrapper from "../shared/RffInputWrapper";
import RffSelectAutocompleteField from "./RffSelectAutocompleteField";
import RffTextField from "./RffTextField";

/**
 * RffAddressEditingField — address lookup that stores a STRUCTURED object, not a
 * string:
 *
 *   { fullAddress, streetNumber, streetName, unit, city, state, zip,
 *     county, country, countryCode, latitude, longitude }
 *
 * The structure is load-bearing, not a convenience: the credit pull matches a
 * borrower on streetNumber / streetName / city / state / zip individually, so a
 * single formatted line would be unusable downstream. That is also why the
 * component always offers manual editing — autocomplete regularly misses unit
 * numbers and occasionally splits a street wrong, and the applicant has to be
 * able to fix it.
 *
 * Backed by Azure Maps rather than Google Places, matching the LOS. Two reasons
 * beyond parity: it's a plain fetch, so there's no third-party script to load
 * and no dependency on Google's legacy `places.Autocomplete` widget (closed to
 * new projects since March 2025); and the suggestions render in a MUI
 * Autocomplete we own, rather than Google's own `.pac-container` popup that has
 * to be fought for stacking order.
 */
const RffAddressEditingField = ({
  name,
  label,
  required = false,
  helperText,
  suppressGrid,
  size,
  disabled,
  onChange,
}) => (
  <RffInputWrapper
    label={label}
    suppressGrid={suppressGrid}
    size={size}
    helperText={helperText}
    required={required}
  >
    <Field
      name={name}
      validate={(v) =>
        required && !v?.fullAddress && !v?.streetName
          ? "This field is required"
          : undefined
      }
      subscription={{ value: true, error: true, touched: true }}
      render={({ input, meta }) => (
        <AddressControl
          name={name}
          input={input}
          meta={meta}
          disabled={disabled}
          onChange={onChange}
        />
      )}
    />
  </RffInputWrapper>
);

const AddressControl = ({ name, input, meta, disabled, onChange }) => {
  const [isEditing, setIsEditing] = useState(false);
  // Set when the applicant chooses to type the address themselves. Tracked
  // separately from the value because at that moment there IS no value yet —
  // keying off the value alone would bounce them straight back to the search.
  const [manualEntry, setManualEntry] = useState(false);
  const hasValue = Boolean(input.value?.fullAddress || input.value?.streetName);

  const commit = (address) => {
    input.onChange(address);
    if (onChange) onChange(address);
  };

  if (!hasValue && !manualEntry) {
    return (
      <AddressSearchInput
        meta={meta}
        disabled={disabled}
        onSelect={(address) => {
          commit(address);
          setIsEditing(false);
        }}
        onManualEntry={() => {
          setManualEntry(true);
          setIsEditing(true);
        }}
      />
    );
  }

  return (
    <Box
      sx={{
        border: `1px solid ${colors.slate300}`,
        // Not `borderRadius: 1` — that resolves to theme.shape (8px) and left
        // this box 2px squarer than the real inputs stacked under it.
        borderRadius: INPUT_BORDER_RADIUS,
        overflow: "hidden",
      }}
    >
      <Flx between centerY gap={0.5} fullWidth>
        <Box
          onClick={() => setIsEditing(true)}
          sx={{
            flex: 1,
            minWidth: 0,
            cursor: "text",
            p: "12px 14px",
            "&:hover": { background: colors.slate50 },
          }}
        >
          <Txt sx={{ color: colors.slate800, fontWeight: 500 }}>
            <DisplayedFullAddress name={name} />
          </Txt>
        </Box>
        <Flx sx={{ pl: "14px", pr: "8.5px" }}>
          {isEditing ? (
            <Tooltip title="Done editing">
              <IconButton
                size="small"
                color="primary"
                onClick={() => setIsEditing(false)}
              >
                <KeyboardArrowUpOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Edit address">
              <IconButton size="small" onClick={() => setIsEditing(true)}>
                <EditOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Clear address">
            <IconButton
              size="small"
              color="error"
              onClick={() => {
                commit(null);
                setIsEditing(false);
                setManualEntry(false);
              }}
            >
              <DeleteOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        </Flx>
      </Flx>

      <Collapse in={isEditing} unmountOnExit>
        <Flx
          column
          gap={2}
          sx={{ p: "14px", borderTop: `1px solid ${colors.slate300}` }}
        >
          <FlxGrid gap={2} grow={1} sx={{ gridTemplateColumns: "120px 1fr" }}>
            <RffTextField
              suppressGrid
              name={`${name}.streetNumber`}
              label="Street Number"
            />
            <RffTextField
              suppressGrid
              name={`${name}.streetName`}
              label="Street Name"
            />
          </FlxGrid>
          <FlxGrid gap={2} grow={1} width="200px">
            <RffTextField
              suppressGrid
              name={`${name}.unit`}
              label="Apartment, Suite, Unit, etc..."
            />
            <RffTextField suppressGrid name={`${name}.city`} label="City" />
            <RffSelectAutocompleteField
              suppressGrid
              name={`${name}.state`}
              label="State"
              options={selectOptionsUsaStates}
            />
            <RffTextField suppressGrid name={`${name}.zip`} label="Zip" />
          </FlxGrid>
        </Flx>
      </Collapse>
    </Box>
  );
};

/**
 * Reads the live value so the summary line reflects hand-edits immediately —
 * the edit panel writes to `${name}.city` etc., which never round-trips through
 * the parent field's own `input.value`.
 */
const DisplayedFullAddress = ({ name }) => {
  const { values } = useFormState({ subscription: { values: true } });
  return useMemo(
    () => formatAddressObjectFullAddress(get(values, name)),
    [values, name],
  );
};

const MIN_QUERY_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 500;

const AddressSearchInput = ({ meta, disabled, onSelect, onManualEntry }) => {
  const { azureMapsKey } = useEnv();
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [noResults, setNoResults] = useState(false);

  const searchAddresses = useCallback(
    async (query) => {
      if (!azureMapsKey || !query || query.length < MIN_QUERY_LENGTH) {
        setOptions([]);
        setNoResults(false);
        return;
      }

      setLoading(true);
      setNoResults(false);

      try {
        let addresses = await fetchAzure("address", query, azureMapsKey);
        // The structured endpoint misses partial or unusual entries; fuzzy is
        // the second pass rather than the first because it returns POIs and
        // other non-address results that would clutter a clean match.
        if (addresses.length === 0) {
          addresses = await fetchAzure("fuzzy", query, azureMapsKey);
        }
        setOptions(addresses);
        setNoResults(addresses.length === 0);
      } catch (err) {
        console.error("Address search failed", err);
        setOptions([]);
        setNoResults(true);
      } finally {
        setLoading(false);
      }
    },
    [azureMapsKey],
  );

  const debouncedSearch = useMemo(
    () => debounce(searchAddresses, SEARCH_DEBOUNCE_MS),
    [searchAddresses],
  );

  useEffect(() => {
    if (inputValue) {
      debouncedSearch(inputValue);
    } else {
      setOptions([]);
      setNoResults(false);
    }
    return () => debouncedSearch.cancel();
  }, [inputValue, debouncedSearch]);

  return (
    <Flx column gap={0.75}>
      <Autocomplete
        freeSolo
        disabled={disabled}
        options={options}
        loading={loading}
        open={inputValue.length >= MIN_QUERY_LENGTH && (options.length > 0 || noResults)}
        inputValue={inputValue}
        onInputChange={(event, next, reason) => {
          if (reason !== "input" && reason !== "clear" && reason !== "reset") return;
          setInputValue(next);
          // Pasting a whole address shouldn't wait out the debounce.
          if (event?.nativeEvent?.inputType === "insertFromPaste") {
            searchAddresses(next);
          }
        }}
        onChange={(event, selected) => {
          if (!selected || typeof selected === "string") return;
          setInputValue(selected.fullAddress);
          onSelect(selected);
        }}
        getOptionLabel={(option) =>
          typeof option === "string" ? option : option.fullAddress || ""
        }
        isOptionEqualToValue={(option, value) => option.id === value?.id}
        // Azure already ranked and filtered these; MUI re-filtering by substring
        // would drop valid matches whose formatting differs from what was typed.
        filterOptions={(x) => x}
        noOptionsText={null}
        loadingText="Searching addresses…"
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            <Box>
              <Txt component="div" variant="body2">
                {option.fullAddress}
              </Txt>
              {option.city && option.state && (
                <Txt component="div" variant="caption" color="text.secondary">
                  {`${option.city}, ${option.state} ${option.zip}`}
                </Txt>
              )}
            </Box>
          </li>
        )}
        PaperComponent={({ children, ...props }) => (
          <Paper {...props}>
            {children}
            {noResults && !loading && <NoResults query={inputValue} />}
          </Paper>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Enter an address (e.g., 123 Main St, Boston, MA)"
            error={Boolean(meta.touched && meta.error)}
            helperText={meta.touched && meta.error ? meta.error : null}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start" sx={{ ml: 0.5, mr: 0 }}>
                  <SearchOutlined sx={{ color: colors.slate400 }} />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      <Txt
        component="button"
        type="button"
        onClick={onManualEntry}
        sx={{
          alignSelf: "flex-start",
          border: "none",
          background: "none",
          p: 0,
          cursor: "pointer",
          fontSize: 12.5,
          color: "primary.main",
          textDecoration: "underline",
        }}
      >
        Can't find it? Enter the address manually
      </Txt>
    </Flx>
  );
};

const NoResults = ({ query }) => (
  <Paper elevation={0} sx={{ p: 2, textAlign: "center" }}>
    <LocationOffOutlined sx={{ fontSize: 40, color: colors.slate300, mb: 1 }} />
    <Txt component="div" variant="body2" color="text.secondary">
      {`No US addresses found for "${query}"`}
    </Txt>
    <Txt component="div" variant="caption" color="text.secondary">
      Try adding more details like city or state
    </Txt>
  </Paper>
);

/**
 * One Azure Maps search, mapped into the structured shape the backend reads.
 *
 * `countrySet=US` is a hard filter rather than a ranking hint — every product
 * these forms feed is US-only, and a Canadian near-match would sail through
 * validation and fail much later at the credit pull.
 */
const fetchAzure = async (kind, query, key) => {
  const url =
    `https://atlas.microsoft.com/search/${kind}/json` +
    `?api-version=1.0&subscription-key=${key}` +
    `&query=${encodeURIComponent(query)}` +
    `&limit=10&countrySet=US${kind === "address" ? "&typeahead=true" : ""}`;

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Azure Maps ${kind} search failed (${response.status})`);
  }
  const data = await response.json();

  return (data.results || [])
    .filter((r) => r.address?.freeformAddress && r.address?.countryCode === "US")
    .map((result, index) => ({
      // Azure ids repeat across the two endpoints; the index keeps React keys
      // and isOptionEqualToValue unambiguous within one result set.
      id: `${result.id || "result"}-${index}`,
      fullAddress: result.address?.freeformAddress || "",
      streetNumber: result.address?.streetNumber || "",
      streetName: result.address?.streetName || "",
      unit: "",
      city: result.address?.localName || result.address?.municipality || "",
      county: result.address?.countrySecondarySubdivision || "",
      state: result.address?.countrySubdivisionCode || "",
      zip: result.address?.postalCode || "",
      country: result.address?.country || "",
      countryCode: result.address?.countryCode || "",
      latitude: result.position?.lat ?? null,
      longitude: result.position?.lon ?? null,
    }));
};

export default RffAddressEditingField;
